/**
 * @fileoverview Content component.core
 */
'use strict';
// React Import
import React, { Component } from 'react';
import {
  BrowserRouter,
  Route,
  Link,
  Switch,
  Redirect,
  withRouter
} from 'react-router-dom';
import PropTypes from 'prop-types';
// Library Import
import _ from 'lodash';
// Core Import
import Loading from './Loading';
// Util Import
import * as ConstUtil from '../../util/ConstUtil';
import * as LogUtil from '../../util/LogUtil';

/**
 * Content共通コンポーネント
 *
 * @class Main
 * @extends {Component}
 */
class Content extends Component {
  constructor(props) {
    super(props);

    _.bindAll(this, 'changeRadioButton', 'changeValue');
  }

  /**
   * ローディング表示
   *
   * @memberof Content
   */
  showLoading() {
    Loading.show();
  }

  /**
   * ローディング非表示
   *
   * @memberof Content
   */
  hideLoading() {
    Loading.hide();
  }

  /**
   * 通信失敗時共通処理
   *
   * @param {Object} err エラー
   * @returns
   * @memberof Content
   */
  failureConnection(err) {
    this.hideLoading();
    // エラーオブジェクトがない場合
    if (_.isEmpty(err) || _.isEmpty(err.response)) {
      // メッセージ画面に遷移
      this.props.history.push(
        ConstUtil.Common.COMPONENT.MESSAGE.URL +
          this.createQueryParam([
            {
              key: 'main',
              value: 'COMMON.ID701'
            }
          ])
      );
      return;
    }
    // 認証エラーの場合
    if (
      err.response.status ===
      ConstUtil.Common.HTTP_STATUS_CODE_LIST.UNAUTHORIZED
    ) {
      localStorage.clear();
      // ログイン画面遷移
      this.props.history.push(ConstUtil.Common.COMPONENT.LOGIN.URL);
      return;
    }
    // メッセージ画面に遷移
    this.props.history.push(
      ConstUtil.Common.COMPONENT.MESSAGE.URL +
        this.createQueryParam([
          {
            key: 'main',
            value: 'COMMON.ID701'
          }
        ])
    );
  }

  /**
   * メールアドレスの入力チェック
   *
   * @param {String} mailAddress メールアドレス
   * @param {Boolean} isRequired 必須項目
   * @memberof Content
   */
  checkMailAddressInput(mailAddress, isRequired) {
    // 空チェック(必須項目の場合)
    if (isRequired) {
      if (_.isEmpty(mailAddress)) {
        return ConstUtil.Message.COMMON.ID727 + ConstUtil.Message.COMMON.ID702;
      }
    }
    // 入力値がある場合
    if (!_.isEmpty(mailAddress)) {
      if (
        !mailAddress.match(ConstUtil.Common.REGULAR_EXPRESSION_PATTERN.P005)
      ) {
        return ConstUtil.Message.COMMON.ID727 + ConstUtil.Message.COMMON.ID703;
      }
    }
    return '';
  }

  /**
   * ファイルダウンロード
   *
   * @param {Array} documentList ドキュメントリスト
   * @memberof Content
   */
  fileDownLoad(documentList) {
    for (let documentItem of documentList) {
      // ダウンロードリンク要素を作成
      let downloadElement = document.createElement('a');
      downloadElement.download = documentItem.fileKey;
      downloadElement.href = documentItem.url;
      // 要素をクリックしダウンロードを実行
      downloadElement.click();
    }
  }

  /**
   * 削除用チェックリストのクリア
   *
   * @memberof Content
   */
  clearCheckList() {
    this.setState({
      checkList: []
    });
  }

  /**
   * クエリパラメータ作成
   *
   * @param {Array}} params パラメータリスト
   * @returns {String} クエリパラメータ文字列
   * @memberof Content
   */
  createQueryParam(params) {
    // 初期値
    let queryString = ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK;
    // クエリパラメータ文字列生成
    for (let param of params) {
      queryString +=
        param.key +
        ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL +
        btoa(encodeURIComponent(param.value)) +
        ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.AND;
    }
    // 最終の「&」を削除
    return queryString.slice(0, -1);
  }

  /**
   * クエリパラメータリスト取得
   *
   * @returns {Array}  クエリパラメータリスト
   * @memberof Content
   */
  getQueryParams() {
    let hash = window.location.hash;
    let index = hash.indexOf(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK
    );
    // hashにクエリパラメータが存在しない場合
    if (index === -1) {
      return [];
    }
    // '?'以降を取得
    let queryString = hash.slice(index + 1);
    // '&'で分割
    let stringArray = queryString.split(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.AND
    );
    // 1パラメータを連想配列に変換
    let paramsArray = [];
    for (let string of stringArray) {
      paramsArray.push({
        key: this.getQueryKey(string),
        value: this.getQueryValue(string)
      });
    }
    return paramsArray;
  }

  /**
   * クエリパラメータのキー取得
   *
   * @param {String} param パラメータ
   * @returns {String} キー
   * @memberof Content
   */
  getQueryKey(param) {
    let index = param.indexOf(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL);
    if (index === -1) {
      return '';
    }
    return param.slice(0, index);
  }

  /**
   * クエリパラメータの値取得
   *
   * @param {String} param パラメータ
   * @returns {String} 値
   * @memberof Content
   */
  getQueryValue(param) {
    let index = param.indexOf(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL);
    if (index === -1) {
      return '';
    }
    return decodeURIComponent(atob(param.slice(index + 1)));
  }

  /**
   * メッセージ定義のキー取得
   *
   * @param {String} queryValue クエリパラメータの値
   * @returns {Array} MessageConstのキーリスト
   * @memberof Content
   */
  getMessageKeys(queryValue) {
    return queryValue.split(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.PERIOD);
  }

  /**
   * 権限チェック
   *
   * @returns
   * @memberof Content
   */
  checkAuthority() {
    // 権限取得
    let authorityId = localStorage.getItem(
      ConstUtil.Common.STORAGE_KEY.AUTHORITY_ID
    );
    // 権限がない場合
    if (_.isEmpty(authorityId)) {
      localStorage.clear();
      // ログイン画面に遷移
      this.props.history.push(ConstUtil.Common.COMPONENT.LOGIN.URL);
      return;
    }
    // 現在のhashを取得
    let hash = window.location.hash;
    // クエリパラメータを含む場合
    let index = hash.indexOf(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK
    );
    if (index !== -1) {
      hash = hash.replace(hash.slice(index), '');
    }
    // hashからCOMPONENTを特定
    let componentList = _.cloneDeep(ConstUtil.Common.COMPONENT);
    let targetComponent = _.find(componentList, { URL: hash });
    // 該当画面が存在しない場合
    if (_.isEmpty(targetComponent)) {
      // ログイン情報削除
      localStorage.clear();
      // ログイン画面に遷移
      this.props.history.push(ConstUtil.Common.COMPONENT.LOGIN.URL);
      return;
    }
    // 権限ありの場合
    if (targetComponent.AUTHORITY_ID.indexOf(authorityId) !== -1) {
      return;
    }
    // ログイン情報削除
    localStorage.clear();
    // ログイン画面に遷移
    this.props.history.push(ConstUtil.Common.COMPONENT.LOGIN.URL);
  }

  /**
   * 入力値変更
   *
   * @param {Object} event イベント
   * @memberof Content
   */
  changeValue(event) {
    if (_.isEmpty(event)) {
      return;
    }
    if (_.isEmpty(event.target)) {
      return;
    }
    this.setState({
      [event.target.id]: event.target.value
    });
  }

  /**
   * ラジオボタン変更
   *
   * @param {Object} event イベント
   * @memberof Content
   */
  changeRadioButton(event) {
    if (_.isEmpty(event)) {
      return;
    }
    if (_.isEmpty(event.target)) {
      return;
    }
    this.setState({
      [event.target.name]: event.target.value
    });
  }
}

Content.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  })
};
export default Content;



// WEBPACK FOOTER //
// ./src/js/component/core/Content.jsx